import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Beer, ArrowRight, ArrowLeft, Users, DollarSign, CircleDollarSign, Footprints, Hourglass } from 'lucide-react';
import './BeerPriceModal.css';
import './CrowdLevelModal.css';

type BeerType = 
  | 'VB' | 'Carlton Draught' | 'Tooheys New' | 'Tooheys Old' | 'XXXX Gold' | 'Great Northern'
  | 'Crown Lager' | 'Pure Blonde' | 'Hahn Super Dry' | 'Coopers Pale Ale' | 'Coopers Sparkling Ale'
  | 'Swan Draught' | 'Emu Export' | 'Melbourne Bitter' | 'Boags Premium' | 'Cascade Premium'
  | 'James Squire' | 'Little Creatures' | 'Stone & Wood' | 'Balter' | 'Young Henrys'
  | 'Peroni' | 'Corona' | 'Heineken' | 'Stella Artois' | 'Asahi' | 'Kirin' | 'Sapporo'
  | 'Guinness' | 'Kilkenny' | 'Other';

type BeerSize = 'pint' | 'schooner';
type CrowdLevel = 'low' | 'moderate' | 'high' | 'very high';

interface BeerPriceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { beer: BeerType; size: BeerSize; price: number; crowd: CrowdLevel; hasHappyHour: boolean; happyHourRange?: string; customBeerName?: string }) => void;
}

// Helper function to get beer logo URLs (multiple sources with fallbacks)
// COMMENTED OUT: Using Beer icon for all beers instead
/*
const getBeerLogoUrls = (beerName: string): string[] => {
  // Map beer names to identifiers for logo services
  const logoMap: Record<string, { domain?: string; slug?: string; brandName?: string }> = {
    'VB': { domain: 'carltonandunited.com.au', slug: 'victoria-bitter', brandName: 'VB' },
    'Carlton Draught': { domain: 'carltonandunited.com.au', slug: 'carlton-draught', brandName: 'Carlton Draught' },
    'Tooheys New': { domain: 'carltonandunited.com.au', slug: 'tooheys', brandName: 'Tooheys' },
    'Tooheys Old': { domain: 'carltonandunited.com.au', slug: 'tooheys', brandName: 'Tooheys' },
    'XXXX Gold': { domain: 'carltonandunited.com.au', slug: 'xxxx', brandName: 'XXXX' },
    'Great Northern': { domain: 'greatnorthern.com.au', slug: 'great-northern', brandName: 'Great Northern' },
    'Crown Lager': { domain: 'carltonandunited.com.au', slug: 'crown-lager', brandName: 'Crown Lager' },
    'Pure Blonde': { domain: 'carltonandunited.com.au', slug: 'pure-blonde', brandName: 'Pure Blonde' },
    'Hahn Super Dry': { domain: 'carltonandunited.com.au', slug: 'hahn', brandName: 'Hahn' },
    'Coopers Pale Ale': { domain: 'coopers.com.au', slug: 'coopers', brandName: 'Coopers' },
    'Coopers Sparkling Ale': { domain: 'coopers.com.au', slug: 'coopers', brandName: 'Coopers' },
    'Swan Draught': { domain: 'carltonandunited.com.au', slug: 'swan', brandName: 'Swan' },
    'Emu Export': { domain: 'carltonandunited.com.au', slug: 'emu', brandName: 'Emu' },
    'Melbourne Bitter': { domain: 'carltonandunited.com.au', slug: 'melbourne-bitter', brandName: 'Melbourne Bitter' },
    'Boags Premium': { domain: 'jboag.com.au', slug: 'boags', brandName: 'Boags' },
    'Cascade Premium': { domain: 'carltonandunited.com.au', slug: 'cascade', brandName: 'Cascade' },
    'James Squire': { domain: 'malt-shovel.com.au', slug: 'james-squire', brandName: 'James Squire' },
    'Little Creatures': { domain: 'littlecreatures.com.au', slug: 'little-creatures', brandName: 'Little Creatures' },
    'Stone & Wood': { domain: 'stoneandwood.com.au', slug: 'stone-wood', brandName: 'Stone & Wood' },
    'Balter': { domain: 'balter.com.au', slug: 'balter', brandName: 'Balter' },
    'Young Henrys': { domain: 'younghenrys.com', slug: 'young-henrys', brandName: 'Young Henrys' },
    'Peroni': { domain: 'peroni.com', slug: 'peroni', brandName: 'Peroni' },
    'Corona': { domain: 'corona.com', slug: 'corona', brandName: 'Corona' },
    'Heineken': { domain: 'heineken.com', slug: 'heineken', brandName: 'Heineken' },
    'Stella Artois': { domain: 'stellaartois.com', slug: 'stella-artois', brandName: 'Stella Artois' },
    'Asahi': { domain: 'asahibeer.com', slug: 'asahi', brandName: 'Asahi' },
    'Kirin': { domain: 'kirin.com', slug: 'kirin', brandName: 'Kirin' },
    'Sapporo': { domain: 'sapporobeer.com', slug: 'sapporo', brandName: 'Sapporo' },
    'Guinness': { domain: 'guinness.com', slug: 'guinness', brandName: 'Guinness' },
    'Kilkenny': { domain: 'guinness.com', slug: 'kilkenny', brandName: 'Kilkenny' },
  };
  
  const mapping = logoMap[beerName];
  if (!mapping) return [];
  
  const urls: string[] = [];
  
  // Try local assets first (if you add logos to public/beer-logos/)
  // To add logos: create /public/beer-logos/ folder and add PNG/SVG files
  if (mapping.slug) {
    urls.push(`/beer-logos/${mapping.slug}.png`);
    urls.push(`/beer-logos/${mapping.slug}.svg`);
  }
  
  // Try online logo services as fallbacks
  if (mapping.domain) {
    // Try Clearbit (most reliable)
    urls.push(`https://logo.clearbit.com/${mapping.domain}`);
    // Try favicon service as last resort (may be low quality)
    urls.push(`https://www.google.com/s2/favicons?domain=${mapping.domain}&sz=256`);
  }
  
  return urls;
};
*/

// Using Beer icon for all beers - logo implementation commented out above
const BEER_TYPES: Array<{ id: BeerType; label: string }> = [
  { id: 'VB', label: 'VB' },
  { id: 'Carlton Draught', label: 'Carlton Draught' },
  { id: 'Tooheys New', label: 'Tooheys New' },
  { id: 'Tooheys Old', label: 'Tooheys Old' },
  { id: 'XXXX Gold', label: 'XXXX Gold' },
  { id: 'Great Northern', label: 'Great Northern' },
  { id: 'Crown Lager', label: 'Crown Lager' },
  { id: 'Pure Blonde', label: 'Pure Blonde' },
  { id: 'Hahn Super Dry', label: 'Hahn Super Dry' },
  { id: 'Coopers Pale Ale', label: 'Coopers Pale Ale' },
  { id: 'Coopers Sparkling Ale', label: 'Coopers Sparkling Ale' },
  { id: 'Swan Draught', label: 'Swan Draught' },
  { id: 'Emu Export', label: 'Emu Export' },
  { id: 'Melbourne Bitter', label: 'Melbourne Bitter' },
  { id: 'Boags Premium', label: 'Boags Premium' },
  { id: 'Cascade Premium', label: 'Cascade Premium' },
  { id: 'James Squire', label: 'James Squire' },
  { id: 'Little Creatures', label: 'Little Creatures' },
  { id: 'Stone & Wood', label: 'Stone & Wood' },
  { id: 'Balter', label: 'Balter' },
  { id: 'Young Henrys', label: 'Young Henrys' },
  { id: 'Peroni', label: 'Peroni' },
  { id: 'Corona', label: 'Corona' },
  { id: 'Heineken', label: 'Heineken' },
  { id: 'Stella Artois', label: 'Stella Artois' },
  { id: 'Asahi', label: 'Asahi' },
  { id: 'Kirin', label: 'Kirin' },
  { id: 'Sapporo', label: 'Sapporo' },
  { id: 'Guinness', label: 'Guinness' },
  { id: 'Kilkenny', label: 'Kilkenny' },
  { id: 'Other', label: 'Other' },
];

const BEER_SIZES: Array<{ id: BeerSize; label: string }> = [
  { id: 'pint', label: 'Pint' },
  { id: 'schooner', label: 'Schooner' },
];

const CROWD_LEVELS: Array<{ id: CrowdLevel; label: string }> = [
  { id: 'low', label: 'Low' },
  { id: 'moderate', label: 'Moderate' },
  { id: 'high', label: 'High' },
  { id: 'very high', label: 'Packed' },
];

// Component for beer type button - using Beer icon for all beers
// COMMENTED OUT: Logo loading implementation (can be restored later)
/*
const BeerTypeButton: React.FC<{
  beer: BeerType;
  label: string;
  logoUrls: string[];
  isSelected: boolean;
  onSelect: () => void;
}> = ({ beer, label, logoUrls, isSelected, onSelect }) => {
  const [currentUrlIndex, setCurrentUrlIndex] = React.useState(0);
  const [logoError, setLogoError] = React.useState(false);

  const currentLogoUrl = logoUrls[currentUrlIndex];
  const hasMoreUrls = currentUrlIndex < logoUrls.length - 1;

  const handleLogoError = () => {
    if (hasMoreUrls) {
      // Try next URL in the fallback chain
      setCurrentUrlIndex(currentUrlIndex + 1);
    } else {
      // All URLs failed, show icon
      setLogoError(true);
    }
  };

  return (
    <motion.button
      className={`beer-type ${isSelected ? 'selected' : ''}`}
      whileHover={{ scale: 1.04 }}
      whileTap={{ scale: 0.98 }}
      onClick={onSelect}
    >
      <div className="beer-icon-circle">
        {currentLogoUrl && !logoError ? (
          <img
            src={currentLogoUrl}
            alt={label}
            className="beer-logo"
            onError={handleLogoError}
            onLoad={(e) => {
              // Ensure logo fits within circle
              const img = e.target as HTMLImageElement;
              img.style.maxWidth = '100%';
              img.style.maxHeight = '100%';
              img.style.objectFit = 'contain';
            }}
          />
        ) : (
          <Beer size={24} />
        )}
      </div>
      <div className="beer-type-title">{label}</div>
    </motion.button>
  );
};
*/

// Simplified version using Beer icon only
const BeerTypeButton: React.FC<{
  beer: BeerType;
  label: string;
  isSelected: boolean;
  onSelect: () => void;
}> = ({ beer, label, isSelected, onSelect }) => {
  return (
    <motion.button
      className={`beer-type ${isSelected ? 'selected' : ''}`}
      whileHover={{ scale: 1.04 }}
      whileTap={{ scale: 0.98 }}
      onClick={onSelect}
    >
      <div className="beer-icon-circle">
        <Beer size={24} />
      </div>
      <div className="beer-type-title">{label}</div>
    </motion.button>
  );
};

const BeerPriceModal: React.FC<BeerPriceModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const [step, setStep] = React.useState<1 | 2 | 3 | 4>(1);
  const [beer, setBeer] = React.useState<BeerType | null>(null);
  const [customBeerName, setCustomBeerName] = React.useState<string>('');
  const [size, setSize] = React.useState<BeerSize | null>(null);
  const [price, setPrice] = React.useState<string>('');
  const [hasHappyHour, setHasHappyHour] = React.useState<boolean>(false);
  const [happyHourRange, setHappyHourRange] = React.useState<string>('');
  const [crowd, setCrowd] = React.useState<CrowdLevel | null>(null);
  const [isFilling, setIsFilling] = React.useState(false);
  const [fillProgress, setFillProgress] = React.useState(0);
  const fillTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const progressIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = React.useRef<number | null>(null);
  const submitDataRef = React.useRef<{
    beer: BeerType | null;
    size: BeerSize | null;
    price: string;
    crowd: CrowdLevel | null;
    hasHappyHour: boolean;
    happyHourRange: string;
    customBeerName: string;
  }>({
    beer: null,
    size: null,
    price: '',
    crowd: null,
    hasHappyHour: false,
    happyHourRange: '',
    customBeerName: '',
  });

  // Keep ref updated with current values
  React.useEffect(() => {
    submitDataRef.current = { beer, size, price, crowd, hasHappyHour, happyHourRange, customBeerName };
  }, [beer, size, price, crowd, hasHappyHour, happyHourRange, customBeerName]);

  // Lock body scroll when modal is open
  React.useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        const scrollY = document.body.style.top;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      };
    }
  }, [isOpen]);

  const canNext = step === 1 
    ? !!beer && (beer !== 'Other' || (beer === 'Other' && customBeerName.trim().length > 0))
    : step === 2 
    ? !!size 
    : step === 3 
    ? !!price && Number(price) > 0 
    : !!crowd;

  const submit = () => {
    if (!beer || !size || !crowd || !price) return;
    if (beer === 'Other' && !customBeerName.trim()) return;
    const parsed = Number(price);
    if (isNaN(parsed) || parsed <= 0) return;
    onSubmit({ 
      beer, 
      size, 
      price: parsed, 
      crowd, 
      hasHappyHour, 
      happyHourRange,
      customBeerName: beer === 'Other' ? customBeerName.trim() : undefined
    });
    onClose();
    setStep(1);
    setBeer(null);
    setCustomBeerName('');
    setSize(null);
    setPrice('');
    setHasHappyHour(false);
    setHappyHourRange('');
    setCrowd(null);
    setIsFilling(false);
    setFillProgress(0);
  };

  const handleSubmit = () => {
    if (!beer || !size || !crowd || !price) return;
    
    // If already filling, submit immediately
    if (isFilling) {
      if (fillTimeoutRef.current) clearTimeout(fillTimeoutRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      setIsFilling(false);
      setFillProgress(0);
      submit();
      return;
    }
  };

  // Start fill animation when crowd level is selected on step 4
  React.useEffect(() => {
    if (step === 4 && crowd && isOpen) {
      // Clear any existing timers first
      if (fillTimeoutRef.current) clearTimeout(fillTimeoutRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      
      // Small delay to ensure UI is ready
      const initTimer = setTimeout(() => {
        // Start fill animation
        setIsFilling(true);
        setFillProgress(0);
        startTimeRef.current = Date.now();
        
        // Update progress every 16ms (60fps)
        progressIntervalRef.current = setInterval(() => {
          if (startTimeRef.current) {
            const elapsed = Date.now() - startTimeRef.current;
            const progress = Math.min((elapsed / 2000) * 100, 100);
            setFillProgress(progress);
            
            if (progress >= 100) {
              if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
            }
          }
        }, 16);
        
        // Auto-submit after 2 seconds
        fillTimeoutRef.current = setTimeout(() => {
          if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
          setIsFilling(false);
          setFillProgress(0);
          // Use latest values from ref
          const data = submitDataRef.current;
          if (data.beer && data.size && data.crowd && data.price) {
            if (data.beer === 'Other' && !data.customBeerName?.trim()) return;
            const parsed = Number(data.price);
            if (!isNaN(parsed) && parsed > 0) {
              onSubmit({
                beer: data.beer,
                size: data.size,
                price: parsed,
                crowd: data.crowd,
                hasHappyHour: data.hasHappyHour,
                happyHourRange: data.happyHourRange,
                customBeerName: data.beer === 'Other' ? data.customBeerName?.trim() : undefined,
              });
              onClose();
              setStep(1);
              setBeer(null);
              setCustomBeerName('');
              setSize(null);
              setPrice('');
              setHasHappyHour(false);
              setHappyHourRange('');
              setCrowd(null);
            }
          }
        }, 2000);
      }, 50);
      
      return () => {
        clearTimeout(initTimer);
        if (fillTimeoutRef.current) clearTimeout(fillTimeoutRef.current);
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      };
    }
  }, [crowd, step, isOpen, onSubmit, onClose]);

  // Reset when crowd selection is cleared or step changes
  React.useEffect(() => {
    if (!crowd || step !== 4) {
      setIsFilling(false);
      setFillProgress(0);
      if (fillTimeoutRef.current) clearTimeout(fillTimeoutRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    }
  }, [crowd, step]);

  // Cleanup on unmount or close
  React.useEffect(() => {
    return () => {
      if (fillTimeoutRef.current) clearTimeout(fillTimeoutRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, []);

  // Reset when modal opens or closes
  React.useEffect(() => {
    if (isOpen) {
      setIsFilling(false);
      setFillProgress(0);
      if (fillTimeoutRef.current) clearTimeout(fillTimeoutRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    } else {
      setStep(1);
      setBeer(null);
      setCustomBeerName('');
      setSize(null);
      setPrice('');
      setHasHappyHour(false);
      setHappyHourRange('');
      setCrowd(null);
      setIsFilling(false);
      setFillProgress(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="recommendations-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="beer-modal"
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Fixed Header */}
          <div className="beer-modal-header">
            <h2>
              {step === 1 ? 'Select beer' : step === 2 ? 'Select size' : step === 3 ? 'Price' : 'Crowd level'}
            </h2>
            <div className="beer-header-actions">
              <button onClick={onClose} className="close-button">
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="beer-modal-content">
            {step === 1 && (
              <div className="beer-types-scroll">
                <div className="beer-types-grid">
                  {BEER_TYPES.map((b) => (
                    <BeerTypeButton
                      key={b.id}
                      beer={b.id}
                      label={b.label}
                      isSelected={beer === b.id}
                      onSelect={() => {
                        setBeer(b.id);
                        if (b.id !== 'Other') {
                          setStep(2);
                        } else {
                          setCustomBeerName('');
                        }
                      }}
                    />
                  ))}
                </div>
                {beer === 'Other' && (
                  <div className="custom-beer-input-container">
                    <label className="custom-beer-label">Enter beer name</label>
                    <input
                      type="text"
                      value={customBeerName}
                      onChange={(e) => setCustomBeerName(e.target.value)}
                      placeholder="e.g., Local IPA, Craft Lager..."
                      className="custom-beer-input"
                      autoFocus
                      autoComplete="off"
                    />
                  </div>
                )}
              </div>
            )}

            {step === 2 && (
              <div className="beer-sizes-container">
                <div className="beer-sizes-grid">
                  {BEER_SIZES.map((s) => (
                    <motion.button
                      key={s.id}
                      className={`beer-size ${size === s.id ? 'selected' : ''}`}
                      whileHover={{ scale: 1.04 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        setSize(s.id);
                        setStep(3);
                      }}
                    >
                      <div className="beer-size-icon-circle">
                        <Beer size={22} />
                      </div>
                      <div className="beer-size-title">{s.label}</div>
                    </motion.button>
                  ))}
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="beer-price">
                <div className="price-input-row">
                  <label className="row-label"><DollarSign size={16} /> Price</label>
                  <div className="price-input-wrap">
                    <span className="currency">$</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="0.1"
                      min="0"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="price-quick-grid">
                  {[8, 9, 10, 11, 12, 13, 14, 15].map((p) => (
                    <button
                      key={p}
                      className={`price-chip ${Number(price) === p ? 'selected' : ''}`}
                      onClick={() => setPrice(String(p))}
                    >
                      ${p.toFixed(2)}
                    </button>
                  ))}
                </div>

                <div className="happy-hour-toggle">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={hasHappyHour}
                      onChange={(e) => setHasHappyHour(e.target.checked)}
                    />
                    <span>Happy hour?</span>
                  </label>
                </div>

                {hasHappyHour && (
                  <div className="happy-hour-details">
                    <div className="happy-hour-input">
                      <label className="row-label">Happy hour time range</label>
                      <input
                        type="text"
                        value={happyHourRange}
                        onChange={(e) => setHappyHourRange(e.target.value)}
                        placeholder="e.g. 4pm – 6pm"
                      />
                    </div>
                    <div className="happy-hour-chip-grid">
                      {['4pm – 6pm', '5pm – 7pm', '6pm – 8pm', 'All night'].map((range) => (
                        <button
                          key={range}
                          type="button"
                          className={`happy-hour-chip ${happyHourRange === range ? 'selected' : ''}`}
                          onClick={() => setHappyHourRange(range)}
                        >
                          {range}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === 4 && (
              <div className="crowd-options">
                {CROWD_LEVELS.map((lvl) => (
                  <motion.button
                    key={lvl.id}
                    className={`crowd-option modern ${crowd === lvl.id ? 'selected' : ''}`}
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setCrowd(lvl.id)}
                  >
                    <div className={`crowd-icon-circle level-${lvl.id.replace(' ', '-')}`}>
                      <Users size={22} />
                    </div>
                    <div className="crowd-option-title compact">{lvl.label}</div>
                  </motion.button>
                ))}
              </div>
            )}
          </div>

          {/* Fixed Footer */}
          <div className="beer-actions">
            <div className="left-actions">
              {step > 1 && (
                <button
                  className="action-button secondary"
                  onClick={() => setStep((s) => ((s - 1) as 1 | 2 | 3))}
                >
                  <ArrowLeft size={18} />
                  Back
                </button>
              )}
            </div>
            <div className="right-actions">
              {step === 1 && beer === 'Other' ? (
                <button
                  className={`action-button primary ${canNext ? 'active' : ''}`}
                  disabled={!canNext}
                  onClick={() => setStep(2)}
                >
                  Next
                  <ArrowRight size={18} />
                </button>
              ) : step > 1 && step < 4 ? (
                <button
                  className={`action-button primary ${canNext ? 'active' : ''}`}
                  disabled={!canNext}
                  onClick={() => setStep((s) => ((s + 1) as 2 | 3 | 4))}
                >
                  Next
                  <ArrowRight size={18} />
                </button>
              ) : (
                step === 4 ? (
                  <button 
                    className={`action-button primary ${canNext ? 'active' : ''} ${isFilling ? 'filling' : ''}`}
                    disabled={!canNext}
                    onClick={handleSubmit}
                  >
                    <span className="button-content">Submit</span>
                    {isFilling && (
                      <div 
                        className="fill-progress"
                        style={{ width: `${fillProgress}%` }}
                      />
                    )}
                  </button>
                ) : null
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BeerPriceModal;

